import React from 'react';
import { motion } from 'framer-motion';
import { Star, Quote } from 'lucide-react';
type Testimonial = {
  quote: string;
  name: string;
  role: string;
  avatar: string;
};
const testimonials: Testimonial[] = [{
  quote: 'Antes me tomaba casi 4 horas armar un informe completo. Ahora lo hago en menos de la mitad del tiempo y mis clientas me dicen que se ven mucho más profesionales. ¡No puedo creer que no lo haya encontrado antes!',
  name: 'Luciana Herrera',
  role: 'Astróloga • Buenos Aires, 38 años',
  avatar: 'https://images.unsplash.com/photo-1544005313-94ddf0286df2?w=300&h=300&fit=crop&crop=face'
}, {
  quote: 'Estaba agotada de trabajar tanto y ganar lo mismo. Con el sistema pude subir mis precios porque ahora mis entregas tienen otro nivel. Mis clientas notan la diferencia y me recomiendan más.',
  name: 'Natalia Suárez',
  role: 'Astróloga • Córdoba, 42 años',
  avatar: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?w=300&h=300&fit=crop&crop=face'
}, {
  quote: 'Lo que más me gustó es que no perdí mi esencia. Los informes siguen siendo míos, pero ahora están organizados y se ven increíbles. Recuperé tiempo para mi familia y para seguir estudiando.',
  name: 'Julieta Navarro',
  role: 'Astróloga • Mendoza, 35 años',
  avatar: 'https://images.unsplash.com/photo-1494790108377-be9c29b29330?w=300&h=300&fit=crop&crop=face'
}, {
  quote: 'Tenía miedo de que fuera algo muy técnico o difícil de usar. Pero es súper intuitivo. En una semana ya estaba entregando informes con el nuevo formato y mis clientas quedaron encantadas.',
  name: 'Camila Benítez',
  role: 'Astróloga • Rosario, 29 años',
  avatar: 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=300&h=300&fit=crop&crop=face'
}, {
  quote: 'Llevo 15 años haciendo cartas y nunca había encontrado algo así. Me ayudó a profesionalizar mi trabajo sin perder la calidez que mis clientas valoran. Ahora puedo atender más consultas sin sentirme abrumada.',
  name: 'Verónica Molina',
  role: 'Astróloga • Mar del Plata, 52 años',
  avatar: 'https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=300&h=300&fit=crop&crop=face'
}];

// Pre-computed star particle positions to avoid hydration mismatches
const stars = Array.from({
  length: 40
}, (_, i) => ({
  id: i,
  top: `${i * 37 % 100}%`,
  left: `${i * 53 % 100}%`,
  size: i % 3 + 1,
  delay: i % 5 * 0.7,
  duration: 3 + i % 4
}));
function TestimonialCard({
  t,
  index
}: {
  t: Testimonial;
  index: number;
}) {
  return <motion.article initial={{
    opacity: 0,
    y: 24
  }} whileInView={{
    opacity: 1,
    y: 0
  }} viewport={{
    once: true,
    margin: '-60px'
  }} transition={{
    duration: 0.55,
    ease: 'easeOut',
    delay: 0.08 * index
  }} className="relative rounded-2xl p-7 md:p-9 bg-white/[0.04] backdrop-blur-xl border border-white/10 shadow-[0_20px_60px_-15px_rgba(0,0,0,0.7)] overflow-hidden" data-id="element-281" data-mp="1prshco1792ss">
      {/* Gold corner glow */}
      <div className="absolute -top-12 -right-12 w-32 h-32 bg-primary/20 rounded-full blur-3xl pointer-events-none" data-id="element-282" data-mp="aet5azq56j4"></div>
      <div className="absolute -bottom-12 -left-12 w-32 h-32 bg-blue-500/10 rounded-full blur-3xl pointer-events-none" data-id="element-283" data-mp="59zxk0xny738"></div>

      {/* Decorative quote icon */}
      <Quote className="absolute top-5 right-5 w-8 h-8 text-primary/20" aria-hidden="true" data-id="element-284" data-mp="33tgitdgswwnf" />

      <div className="relative z-10" data-id="element-285" data-mp="3dpba0wz3c01t">
        {/* Stars */}
        <div className="flex gap-1 mb-5" aria-label="Calificación 5 de 5 estrellas" data-id="element-286" data-mp="3do02qn6fsa5y">
          {[...Array(5)].map((_, j) => <Star key={j} className="w-4 h-4 fill-primary text-primary drop-shadow-[0_0_4px_rgba(212,168,87,0.6)]" data-id="element-287" data-mp="szu6i7ev3bi7" data-mpi={j} />)}
        </div>

        {/* Quote */}
        <p className="text-foreground/90 text-base md:text-lg leading-relaxed mb-7 italic" data-id="element-288" data-mp="vip8mktlc4aa">
          "{t.quote}"
        </p>

        {/* Author */}
        <div className="flex items-center gap-3 pt-5 border-t border-white/10" data-id="element-289" data-mp="qmid7lytmb6l">
          <div className="relative shrink-0" data-id="element-290" data-mp="15j993l9ad4rg">
            <div className="absolute inset-0 bg-primary/30 rounded-full blur-md" data-id="element-291" data-mp="10iv1g4bljwty"></div>
            <img src={t.avatar} alt={t.name} className="relative w-12 h-12 rounded-full object-cover border-2 border-primary/40 shadow-lg" loading="lazy" data-id="element-292" data-mp="1eicuivvxdw94" />
          </div>
          <div data-id="element-293" data-mp="1o74kiyhfe6he">
            <div className="font-semibold text-foreground" data-id="element-294" data-mp="1o7moyie8rr0e">{t.name}</div>
            <div className="text-xs text-primary/80 uppercase tracking-wider font-medium" data-id="element-295" data-mp="1j8t9wljb3ry4">
              {t.role}
            </div>
          </div>
        </div>
      </div>
    </motion.article>;
}
export function TestimonialsMarquee() {
  const featured = testimonials.slice(0, 3);
  return <section id="testimonios" className="relative py-20 md:py-24 overflow-hidden border-y border-white/5" style={{
    background: 'radial-gradient(ellipse at top, #0d1238 0%, #050518 60%, #020210 100%)'
  }} data-id="element-296" data-mp="2bvb5ky7utdjv">
      {/* Animated star particles */}
      <div className="absolute inset-0 pointer-events-none" data-id="element-297" data-mp="27tg51vxqz475">
        {stars.map(s => <motion.div key={s.id} className="absolute rounded-full bg-white" style={{
        top: s.top,
        left: s.left,
        width: s.size,
        height: s.size
      }} animate={{
        opacity: [0.2, 1, 0.2],
        scale: [0.8, 1.4, 0.8]
      }} transition={{
        duration: s.duration,
        delay: s.delay,
        repeat: Infinity,
        ease: 'easeInOut'
      }} data-id="element-298" data-mp="1t5bkemf4wilq" />)}
      </div>

      {/* Decorative cosmic glows */}
      <div className="absolute top-1/2 left-1/4 -translate-y-1/2 w-[500px] h-[500px] bg-primary/5 rounded-full blur-3xl pointer-events-none" data-id="element-299" data-mp="3guzogqwslv9g"></div>
      <div className="absolute top-1/2 right-1/4 -translate-y-1/2 w-[500px] h-[500px] bg-blue-500/10 rounded-full blur-3xl pointer-events-none" data-id="element-300" data-mp="3c2g8xoejoq20"></div>

      <div className="relative z-10 container mx-auto px-4 max-w-3xl" data-id="element-301" data-mp="333l2qcbi3fyp">
        <div className="text-center mb-12 md:mb-14" data-id="element-302" data-mp="3318fbjmz4de9">
          <h2 className="text-2xl md:text-4xl font-heading font-bold mb-4 text-foreground leading-tight uppercase tracking-wide" data-id="element-303" data-mp="3mmudf3rxonl">
            Lo que dicen las{' '}
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-primary to-amber-200" data-id="element-304" data-mp="3qsdurxp053o1">
              astrólogas
            </span>
          </h2>
          <p className="text-base md:text-lg text-muted-foreground leading-relaxed max-w-2xl mx-auto" data-id="element-305" data-mp="3lvnd55xju00i">
            Profesionales que ya transformaron su práctica.
          </p>
        </div>

        <div className="space-y-6 md:space-y-7" data-id="element-306" data-mp="2ad2s8q8g6cb9">
          {featured.map((t, i) => <TestimonialCard key={t.name} t={t} index={i} data-id="element-307" data-mp="20c0f567jn6ff" data-mpi={i} />)}
        </div>
      </div>
    </section>;
}